import { useCallback } from 'react';

import type { Reference } from '@apollo/client';
import { gql } from '@apollo/client';
import { Apollo } from 'src/gql/generated';

/** Lists every playbook available to the current user (org-scoped server-side). */
export function useGetPlaybooks(options?: Parameters<typeof Apollo.useGetPlaybooksQuery>[0]) {
  const { data, loading, refetch } = Apollo.useGetPlaybooksQuery(options);
  return { loading, playbooks: data?.playbooks ?? [], refetch };
}

/** Fetches a single playbook with its rules for the detail page. */
export function useGetPlaybook(
  playbookId: UUID,
  options?: Omit<NonNullable<Parameters<typeof Apollo.useGetPlaybookDetailQuery>[0]>, 'variables'>
) {
  const { data, loading, refetch } = Apollo.useGetPlaybookDetailQuery({
    ...options,
    variables: { id: playbookId },
  });
  return { loading, playbook: data?.playbook ?? null, refetch };
}

/** Creates a playbook template. */
export function useCreatePlaybook() {
  const [createPlaybook, { loading }] = Apollo.useCreatePlaybookMutation();
  const safeCreatePlaybook = useCallback(
    (options: Parameters<Apollo.CreatePlaybookMutationHookResult[0]>[0]) => {
      return createPlaybook({
        ...options,
        update(cache, { data }) {
          const newPlaybookRef = cache.writeFragment({
            data: data?.createPlaybook,
            fragment: gql`
              fragment NewPlaybook on Playbook {
                __typename
                id
              }
            `,
          });
          if (!newPlaybookRef) return;

          cache.modify({
            fields: {
              playbooks: (refs = []) => {
                return [...refs, newPlaybookRef];
              },
            },
          });
        },
      });
    },
    [createPlaybook]
  );

  return {
    createPlaybook: safeCreatePlaybook,
    loading,
  };
}

/**
 * Updates a playbook's title or description. The mutation returns the updated
 * playbook, which Apollo normalizes by id, so no manual cache write is needed.
 */
export function useUpdatePlaybook() {
  const [updatePlaybook, { loading }] = Apollo.useUpdatePlaybookMutation();
  const safeUpdatePlaybook = useCallback(
    (input: Apollo.UpdatePlaybookInput) => {
      return updatePlaybook({ variables: { input } });
    },
    [updatePlaybook]
  );

  return { loading, updatePlaybook: safeUpdatePlaybook };
}

/** Deletes a playbook template. */
export function useDeletePlaybook() {
  const [deletePlaybook] = Apollo.useDeletePlaybookMutation();
  const safeDeletePlaybook = useCallback(
    (playbookId: UUID) => {
      return deletePlaybook({
        update(cache) {
          cache.modify({
            fields: {
              playbooks: (refs: readonly Reference[] = [], { readField }) => {
                return refs.filter(ref => readField<UUID>('id', ref) !== playbookId);
              },
            },
          });
        },
        variables: { playbookId },
      });
    },
    [deletePlaybook]
  );

  return {
    deletePlaybook: safeDeletePlaybook,
  };
}

export function useDuplicatePlaybook() {
  const [duplicatePlaybook, { loading }] = Apollo.useDuplicatePlaybookMutation();

  const safeDuplicatePlaybook = useCallback(
    (playbookId: UUID) => {
      return duplicatePlaybook({
        update(cache, { data }) {
          cache.modify({
            fields: {
              playbooks: (refs = []) => {
                if (!data) return refs;
                return [
                  ...refs,
                  cache.writeFragment({
                    data: data.duplicatePlaybook,
                    fragment: gql`
                      fragment NewPlaybook on Playbook {
                        __typename
                        id
                      }
                    `,
                  }),
                ];
              },
            },
          });
        },
        variables: { playbookId },
      });
    },
    [duplicatePlaybook]
  );

  return {
    duplicatePlaybook: safeDuplicatePlaybook,
    loading,
  };
}

/**
 * The org's playbooks for the Playbooks drawer, each with its run status
 * against the document the drawer is open on — which the query needs, so the
 * options carrying it are not optional.
 */
export function useGetPlaybooksList(
  options: Parameters<typeof Apollo.useGetPlaybooksListQuery>[0]
) {
  const { data, loading, refetch } = Apollo.useGetPlaybooksListQuery(options);
  return { loading, playbooks: data?.playbooks ?? [], refetch };
}
